import { Box, Checkbox, Grid, Typography } from '@material-ui/core'
import { CustomTooltip } from 'components/facets'
import { LanguageFlag } from 'components/facets/LanguageFlag'
import NewWindow from 'components/facets/NewWindow'
import { changeOrderProp, removeOrderFromPickingList, solveAllIssues } from 'lib/api'
import { useFillViewportHeight, useOrderStore } from 'lib/hooks'
import { useFetchOrders } from 'lib/hooks/useFetchOrders'
import { getGameCodeFromName, getPickingId, getPickingList, getPickingProgress } from 'lib/utils'
import { useSnackbar } from 'notistack'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AutoSizer, Column, SortDirection, SortDirectionType, Table } from 'react-virtualized'
import {
  ChangeOrderPropsWrapper,
  GAME_NAMES,
  getOrderIssues,
  getOrderState,
  hasIssues,
  hasUnresolvedIssues,
  Order,
  orderContainsSealed,
  ORDER_SIDEBAR_TABS,
} from 'shared'
import { BACKOFFICE_ORDERS_TABLE_HEIGHT } from 'utils/constants'
import { ArticlesTable } from './ArticlesTable'
import { ColorBox } from './ColorBox'
import { CompactOrdersTable } from './CompactOrdersTable'
import { OrderDetailsDialog } from './OrderDetailsDialog'
import { OrderInfo } from './OrderInfo'
import { ReportIssueDialog } from './ReportIssueDialog'
import { SolveIssueDialog } from './SolveIssueDialog'
import { StatusLabel } from './StatusLabel'
import { TrackingNumberDialog } from './TrackingNumberDialog'
import { UnlinkOrderDialog } from './UnlinkOrderDialog'

export interface OrdersTableProps {
  handleUpdateOrders: (updatedOrders: Order[]) => void
  handleChangeOrderState: (orderId: number, newState: string) => void
  // backoffice: show the orders without any action that changes them
  viewOnly?: boolean
}

export const OrdersTable = ({
  handleUpdateOrders,
  handleChangeOrderState,
  viewOnly,
}: OrdersTableProps): JSX.Element => {
  const { t } = useTranslation()

  const {
    filteredOrders,
    selectedArticle,
    selectedOrderIds,
    selectedOrders,
    activeTab,
    expandedOrder,
    setSelectedArticle,
    setSelectedOrderIds,
    setSelectedOrders,
    setExpandedOrder,
    setExpandedRowIndex,
    setFilteredOrders,
  } = useOrderStore()

  const [selectedOrder, setSelectedOrder] = useState<Order>()
  // const [openArticleDialog, setOpenArticleDialog] = useState<boolean>(false)
  const [openReportIssueDialog, setOpenReportIssueDialog] = useState<boolean>(false)
  const [openSolveIssueDialog, setOpenSolveIssueDialog] = useState<boolean>(false)
  const [openOrderDetailsDialog, setOpenOrderDetailsDialog] = useState<boolean>(false)
  const [openTrackingNumberDialog, setOpenTrackingNumberDialog] = useState<boolean>(false)
  const [editIssueMode, setEditIssueMode] = useState<boolean>(false)
  const [openUnlinkOrderDialog, setOpenUnlinkOrderDialog] = useState<boolean>(false)
  const { enqueueSnackbar } = useSnackbar()

  const { convertOrderResponseDtoToOrderClass } = useFetchOrders()

  const [openMessagesPopup, setOpenMessagesPopup] = useState<boolean>(false)

  const getFlagFromCountry = (country: string | undefined) => {
    if (country === 'D') {
      country = 'DE'
    }

    return <LanguageFlag height="14px" code={country || 'gg'} style={{ alignSelf: 'center' }} />
  }

  const getFormattedDate = (d: Date) => {
    if (d) {
      const localeDate = new Date(d).toLocaleString()
      const [date, time] = localeDate.split(', ')
      return (
        <>
          <div>{date}</div>
          <div>{time}</div>
        </>
      )
    } else return <></>
  }

  const handleCloseReportIssueDialog = () => {
    setSelectedOrder(undefined)
    setSelectedArticle(undefined)
    setEditIssueMode(false)
    setOpenReportIssueDialog(false)
  }

  const handleConfirmReportIssueDialog = async (issue: string, issueCode: string) => {
    if (selectedOrder) {
      const issues = getOrderIssues(selectedOrder)
      const psOrderIssues: ChangeOrderPropsWrapper = {
        ptOrderId: selectedOrder!.ptOrderId,
        orderIssue: {
          id: issues[0]?.id,
          code: issueCode,
          solved: false,
          description: issue,
          solution: '',
        },
      }
      const newOrder = await changeOrderProp(psOrderIssues)
      handleUpdateOrders([convertOrderResponseDtoToOrderClass(newOrder)])
    }
    if (openReportIssueDialog) handleCloseReportIssueDialog()
  }

  const handleCloseSolveIssueDialog = () => {
    setSelectedOrder(undefined)
    setOpenSolveIssueDialog(false)
  }

  /**
   *
   * Mark issues as resolved. If the order has unresolved issues, they are marked as resolved.
   * If the order contains items with unresolved issues, item-level issues are also marked as resolved
   */
  const handleConfirmSolveIssueDialog = async (solution: string) => {
    if (selectedOrder) {
      const newOrder = await solveAllIssues(selectedOrder.ptOrderId, solution)
      handleUpdateOrders([convertOrderResponseDtoToOrderClass(newOrder)])
    }
    if (openSolveIssueDialog) handleCloseSolveIssueDialog()
  }

  const handleConfirmTrackingNumberDialog = async (trackingNumber: string) => {
    if (selectedOrder) {
      const psOrderTracking: ChangeOrderPropsWrapper = {
        ptOrderId: selectedOrder!.ptOrderId,
        tracking: {
          trackingNumber: trackingNumber,
        },
      }

      const newOrder = await changeOrderProp(psOrderTracking)

      handleUpdateOrders([convertOrderResponseDtoToOrderClass(newOrder)])
    }
    if (openTrackingNumberDialog) handleCloseTrackingNumberDialog()
  }

  const handleCloseTrackingNumberDialog = () => {
    setSelectedOrder(undefined)
    setOpenTrackingNumberDialog(false)
  }

  const handleCloseUnlinkOrderDialog = () => {
    setSelectedOrder(undefined)
    setOpenUnlinkOrderDialog(false)
  }

  const handleConfirmUnlinkOrderDialog = async () => {
    if (!selectedOrder) return
    try {
      await handleRemoveOrderFromPickingList(selectedOrder)
    } catch (e) {
      enqueueSnackbar(t('error.generic'), {
        variant: 'error',
      })
    }
    handleCloseUnlinkOrderDialog()
  }

  const handleSelectRow = (e: any, order: Order) => {
    if (e.target.checked) {
      const newSelectedOrderIds = [...selectedOrderIds]
      newSelectedOrderIds.push(order.ptOrderId)
      setSelectedOrderIds(newSelectedOrderIds)

      const newSelectedOrders = [...selectedOrders]
      newSelectedOrders.push(order)
      setSelectedOrders(newSelectedOrders)
    } else {
      if (selectedOrderIds.includes(order.ptOrderId)) {
        const idx = selectedOrderIds.findIndex((id) => id === order.ptOrderId)
        const newSelectedOrderIds = [...selectedOrderIds]
        newSelectedOrderIds.splice(idx, 1)

        const newSelectedOrders = [...selectedOrders]
        newSelectedOrders.splice(idx, 1)
        setSelectedOrderIds(newSelectedOrderIds)
        setSelectedOrders(newSelectedOrders)
      }
    }
  }

  const handleOpenMessagesPopup = () => {
    setOpenMessagesPopup(false)
    setTimeout(() => setOpenMessagesPopup(true), 200)
  }

  const handleExpandRow = (order: Order, rowIndex: number) => {
    setExpandedOrder(order)
    setExpandedRowIndex(rowIndex)
  }

  const handleOpenExpandedOrderDialog = (order: Order, rowIndex: number) => {
    rowIndex
    setSelectedOrder(order)
    setOpenOrderDetailsDialog(true)
  }

  const handleNextOrderDetail = () => {
    if (!selectedOrder) return
    const currentIndex = filteredOrders.findIndex((o) => o.ptOrderId === selectedOrder.ptOrderId)
    if (currentIndex < filteredOrders.length - 1) {
      setSelectedOrder(filteredOrders[currentIndex + 1])
    }
  }

  const handlePrevOrderDetail = () => {
    if (!selectedOrder) return
    const currentIndex = filteredOrders.findIndex((o) => o.ptOrderId === selectedOrder.ptOrderId)
    if (currentIndex > 0) {
      setSelectedOrder(filteredOrders[currentIndex - 1])
    }
  }

  const getArticleIssues = (order: Order) => {
    const articleIssues: string[] = []
    order?.articles?.forEach((pta) => {
      pta.issues.forEach((issue) => {
        articleIssues.push(`${pta.card.name}: ${issue.description}`)
      })
      // const article = order.article.find((a) => a.ptArticleId === pta.ptArticleId)
      // pta.issues?.forEach((i) =>
      //   articleIssues.push(`${article ? article.product.enName : ''}: ${i.description}`)
      // )
    })
    return articleIssues
  }

  const showPickingProgress = (order: Order) => {
    const pickingProgress = getPickingProgress(order)
    if (pickingProgress === -1) {
      return '-'
    } else {
      return `${pickingProgress}%`
    }
  }

  // Fill the viewport below the table instead of hardcoding a `100vh - <magic per tab>`
  // height (which breaks when the filters above wrap onto more rows). Re-measures when the
  // tab, the row count, or the expanded/collapsed state shift the table's top.
  const [tableContainerRef, tableHeight] = useFillViewportHeight<HTMLDivElement>([
    activeTab,
    filteredOrders.length,
    expandedOrder,
  ])

  // columns sorting
  const [sortBy, setSortBy] = useState<string>('datePaid')
  const [sortDirection, setSortDirection] = useState<SortDirectionType>(SortDirection.DESC)
  const sort = ({ sortBy }: { sortBy: string }) => {
    setSortBy(sortBy)
    setSortDirection(sortDirection === SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC)
    let sortedOrders = [...filteredOrders]

    if (sortBy === 'datePaid') {
      sortedOrders = sortedOrders.sort((a, b) => {
        return a.state.datePaid < b.state.datePaid ? 1 : -1
      })
    } else if (sortBy === 'dateSent') {
      sortedOrders = sortedOrders.sort((a, b) => {
        return a.state.dateSent < b.state.dateSent ? 1 : -1
      })
    } else if (sortBy === 'buyer') {
      sortedOrders = sortedOrders.sort((a, b) => {
        return (a.buyer?.username ?? '') < (b.buyer?.username ?? '') ? 1 : -1
      })
    } else if (sortBy === 'list') {
      sortedOrders = sortedOrders.sort((a, b) => {
        return getPickingList(a) < getPickingList(b) ? 1 : -1
      })
    } else if (sortBy === 'pid') {
      sortedOrders = sortedOrders.sort((a, b) => {
        return getPickingId(a) < getPickingId(b) ? 1 : -1
      })
    } else if (sortBy === 'progress') {
      sortedOrders = sortedOrders.sort((a, b) => {
        return getPickingProgress(a) < getPickingProgress(b) ? 1 : -1
      })
    } else if (sortBy === 'shippingMethod') {
      sortedOrders = sortedOrders.sort((a, b) => {
        return (a.shipping?.carrier ?? '') < (b.shipping?.carrier ?? '') ? 1 : -1
      })
    } else if (sortBy === 'orderBin') {
      sortedOrders = sortedOrders.sort((a, b) => {
        const pickingIdA = a.picking ? a.picking.pickingId : null
        const pickingIdB = b.picking ? b.picking.pickingId : null

        if (pickingIdA && pickingIdB) {
          return pickingIdA < pickingIdB ? 1 : pickingIdA > pickingIdB ? -1 : 0
        } else if (pickingIdA) {
          return -1
        } else if (pickingIdB) {
          return 1
        } else {
          return 0
        }
      })
    }

    if (sortDirection === SortDirection.DESC) {
      sortedOrders = sortedOrders.reverse()
    }
    // Persist the sorted order to the store so react-virtualized re-renders the rows.
    // (The returned value is ignored by the Table's `sort` prop, and the old in-place
    // filteredOrders.sort() no longer mutates the store array.)
    setFilteredOrders(sortedOrders)
    return sortedOrders
  }

  const handleRemoveOrderFromPickingList = async (order: Order) => {
    const updatedOrder = await removeOrderFromPickingList(
      order.picking!.pickingList,
      order.ptOrderId
    )
    handleUpdateOrders([convertOrderResponseDtoToOrderClass(updatedOrder)])
  }

  const shippingTooltip = (order: Order) => {
    if (order.source === 'cardtrader') {
      return <></>
    }

    // Shopify tracking cannot be set if the tracking number has been sent and fullfilled
    if (
      order.source == 'shopify' &&
      order.state.state !== 'picking' &&
      order.state.state != 'picked' &&
      order.state.state != 'packed' &&
      order.shipping?.tracking
    ) {
      return <></>
    }
    return (
      <>
        <CustomTooltip title="Tracking number">
          <Box
            data-testid={`add-tracking-number-${order.ptOrderId}`}
            onClick={() => {
              setSelectedOrder(order)
              setOpenTrackingNumberDialog(true)
            }}
          >
            <i className="fa-light fa-tag table-action-icon"></i>
          </Box>
        </CustomTooltip>
      </>
    )
  }

  const disableCheckbox = (order: Order) => {
    if (order.state.state != 'paid' && order.isCtZero) return true
    if (activeTab === ORDER_SIDEBAR_TABS.MANAGE_PICKING_LISTS && order.state.state === 'picking')
      return true
    return false
  }

  return (
    <>
      {expandedOrder ? (
        <Grid container spacing={3}>
          <Grid item xs={5}>
            <CompactOrdersTable handleSelectRow={handleSelectRow} viewOnly={viewOnly} />
          </Grid>
          <Grid item xs={7}>
            <OrderInfo order={expandedOrder} />
            <Box padding={1} />
            <ArticlesTable
              key={expandedOrder.ptOrderId}
              articles={expandedOrder.articles ?? []}
              setOpenReportIssueDialog={setOpenReportIssueDialog}
              viewOnly={viewOnly}
            />
          </Grid>
        </Grid>
      ) : (
        <div className="responsive-table-container">
          <p>Items: {filteredOrders.length}</p>
          <div ref={tableContainerRef} />
          <AutoSizer disableHeight>
            {({ width }) => (
              <Table
                width={Math.max(width, 1300)}
                height={viewOnly ? BACKOFFICE_ORDERS_TABLE_HEIGHT : tableHeight}
                headerHeight={40}
                rowHeight={45}
                rowCount={filteredOrders.length}
                rowGetter={({ index }) => filteredOrders[index]}
                sort={sort}
                sortBy={sortBy}
                sortDirection={sortDirection}
                className={`picking-table `}
              >
                {(activeTab === 1 || activeTab === 3) && (
                  <Column
                    width={160}
                    flexGrow={16}
                    flexShrink={1}
                    label="Select"
                    dataKey=""
                    disableSort={true}
                    cellRenderer={({ rowData: order }) => (
                      <Checkbox
                        checked={selectedOrderIds.includes(order.ptOrderId)}
                        onClick={(e: any) => handleSelectRow(e, order)}
                        data-testid={'select-' + order.sourceOrderId}
                        disabled={disableCheckbox(order)}
                      />
                    )}
                  />
                )}

                <Column
                  width={300}
                  flexGrow={30}
                  flexShrink={1}
                  label="Paid date"
                  dataKey="datePaid"
                  cellRenderer={({ rowData: order }) => getFormattedDate(order.state.datePaid)}
                />

                <Column
                  width={300}
                  flexGrow={30}
                  flexShrink={1}
                  label="Sent date"
                  dataKey="dateSent"
                  cellRenderer={({ rowData: order }) => getFormattedDate(order.state.dateSent)}
                />

                <Column
                  width={400}
                  flexGrow={40}
                  flexShrink={1}
                  label="Buyer"
                  dataKey="buyer"
                  cellRenderer={({ rowData: order }) => (
                    <>
                      <Box display="flex">
                        {getFlagFromCountry(order.shipping?.country)}
                        <Box paddingLeft={1}>
                          <Box>{order.buyer?.username}</Box>
                          <Box>{order.buyer?.address?.name}</Box>
                        </Box>
                      </Box>
                    </>
                  )}
                />

                <Column
                  width={300}
                  label=""
                  dataKey=""
                  disableSort={true}
                  cellRenderer={({ rowData: order }) => (
                    <div>
                      {order.articleGames?.map((game: string) => (
                        <CustomTooltip key={game} title={game ?? 'Unknown'}>
                          <span
                            className={`ss-${getGameCodeFromName(game)} ss-game-icon ss-fw`}
                            style={{ fontSize: '1.5rem' }}
                          ></span>
                        </CustomTooltip>
                      ))}
                    </div>
                  )}
                />

                <Column
                  width={80}
                  flexGrow={8}
                  flexShrink={1}
                  label=""
                  dataKey=""
                  disableSort={true}
                  cellRenderer={({ rowData: order }) => (
                    <div className="d-flex align-items-center justify-content-center">
                      {orderContainsSealed(order) && (
                        <CustomTooltip title="Contains sealed">
                          <i className="fa-light fa-box-taped orders-table-informative-icon"></i>
                        </CustomTooltip>
                      )}
                    </div>
                  )}
                />

                <Column
                  width={80}
                  flexGrow={8}
                  flexShrink={1}
                  label=""
                  dataKey=""
                  disableSort={true}
                  cellRenderer={({ rowData: order }) => (
                    <div className="d-flex align-items-center justify-content-center">
                      {order.isPresale && (
                        <CustomTooltip title="Presale">
                          <i className="fa-light fa-clock-rotate-left orders-table-informative-icon"></i>
                        </CustomTooltip>
                      )}
                    </div>
                  )}
                />

                {activeTab === 3 && (
                  <Column
                    width={500}
                    flexGrow={50}
                    flexShrink={1}
                    label="Shipping method"
                    dataKey="shippingMethod"
                    cellRenderer={({ rowData: order }) => (
                      <div style={{ height: '40px' }} className="text-wrap">
                        <CustomTooltip title={order.shipping?.carrier ?? ''}>
                          <span>{order.shipping?.carrier ?? ''}</span>
                        </CustomTooltip>
                      </div>
                    )}
                  />
                )}

                <Column
                  width={140}
                  flexGrow={14}
                  flexShrink={1}
                  label="Status"
                  dataKey=""
                  disableSort={true}
                  headerClassName="centered-header"
                  cellRenderer={({ rowData: order }) => (
                    <div
                      className="d-flex align-items-center justify-content-center"
                      data-testid={'state-' + order.sourceOrderId}
                    >
                      <StatusLabel
                        type="state"
                        name={getOrderState(order)}
                        state={getOrderState(order)}
                        active={true}
                      ></StatusLabel>
                    </div>
                  )}
                />

                {/* <Column
                width={150}
                label="Order note"
                dataKey=""
                cellRenderer={({ rowData: order, parent, rowIndex }) =>
                  cellRendererMeasurer(parent, rowIndex, <>{order.note}</>)
                }
              /> */}

                <Column
                  width={300}
                  flexGrow={30}
                  flexShrink={1}
                  label="Issues"
                  dataKey=""
                  disableSort={true}
                  headerClassName="centered-header"
                  cellRenderer={({ rowData: order }) => (
                    <div
                      className="d-flex align-items-center justify-content-center"
                      data-testid={'issues-' + order.sourceOrderId}
                    >
                      {hasIssues(order) && (
                        <ColorBox
                          type="prop"
                          name="issues"
                          state="issues"
                          clickable={!viewOnly}
                          numIssues={getOrderIssues(order).length + getArticleIssues(order).length}
                          onClick={() => {
                            setSelectedOrder(order)
                            setOpenReportIssueDialog(true)
                          }}
                        />
                      )}
                    </div>
                  )}
                />

                {!viewOnly && (
                  <Column
                    width={300}
                    flexGrow={30}
                    flexShrink={1}
                    label="Actions"
                    dataKey=""
                    disableSort={true}
                    cellRenderer={({ rowData: order }) => (
                      <div className="d-flex align-items-center">
                        {[0].includes(activeTab) && order.source == 'cardmarket' && (
                          <CustomTooltip title={t('orders.messageBuyer') as string}>
                            <Box
                              onClick={() => {
                                setSelectedOrder(order)
                                handleOpenMessagesPopup()
                              }}
                              alignSelf="center"
                            >
                              <i className="fa-light fa-envelope table-action-icon"></i>
                            </Box>
                          </CustomTooltip>
                        )}

                        {!hasIssues(order) && !order.isCtZero && (
                          <CustomTooltip title={t('orders.addOrderIssue') as string}>
                            <Box
                              data-testid={`add-order-issue-${order.sourceOrderId}`}
                              onClick={() => {
                                setSelectedOrder(order)
                                setOpenReportIssueDialog(true)
                              }}
                            >
                              <i className="fa-light fa-file-lines table-action-icon"></i>
                            </Box>
                          </CustomTooltip>
                        )}

                        {hasUnresolvedIssues(order) && !order.isCtZero && (
                          <>
                            <CustomTooltip title="Solve all">
                              <Box
                                data-testid={`solve-issues-${order.sourceOrderId}`}
                                onClick={() => {
                                  setSelectedOrder(order)
                                  setOpenSolveIssueDialog(true)
                                }}
                              >
                                <i className="fa-light fa-list-check table-action-icon"></i>
                              </Box>
                            </CustomTooltip>
                          </>
                        )}

                        {getOrderState(order) === 'picking' && !order.isCtZero && (
                          <CustomTooltip title={t('orders.unlinkFromPickingList') as string}>
                            <Box
                              data-testid={`unlink-picking-list-${order.sourceOrderId}`}
                              onClick={() => {
                                setSelectedOrder(order)
                                setOpenUnlinkOrderDialog(true)
                              }}
                            >
                              <i className="fa-light fa-link-simple-slash table-action-icon"></i>
                            </Box>
                          </CustomTooltip>
                        )}

                        {[3].includes(activeTab) && (
                          <>
                            {shippingTooltip(order)}

                            {getOrderState(order) === 'picked' && !order.isCtZero && (
                              <CustomTooltip title={t('orders.markAsPacked') as string}>
                                <Box
                                  data-testid={`mark-as-packed-${order.sourceOrderId}`}
                                  onClick={() => {
                                    handleChangeOrderState(order.ptOrderId, 'packed')
                                  }}
                                >
                                  <i className="fa-light fa-box-archive table-action-icon"></i>
                                </Box>
                              </CustomTooltip>
                            )}

                            {order.source != 'cardtrader' && getOrderState(order) === 'packed' && (
                              <CustomTooltip title={t('orders.markAsSent') as string}>
                                <Box
                                  data-testid={`mark-as-sent-${order.sourceOrderId}`}
                                  onClick={() => {
                                    handleChangeOrderState(order.ptOrderId, 'sent')
                                  }}
                                >
                                  <i className="fa-light fa-truck table-action-icon"></i>
                                </Box>
                              </CustomTooltip>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  />
                )}

                {activeTab === 1 && (
                  <Column
                    width={130}
                    label="List"
                    dataKey="list"
                    headerClassName="centered-header"
                    cellRenderer={({ rowData: order }) => (
                      <div className="d-flex align-items-center justify-content-center">
                        {order.picking && order.picking.pickingList
                          ? order.picking.pickingList
                          : '-'}
                      </div>
                    )}
                  />
                )}

                {[1, 3].includes(activeTab) && (
                  <Column
                    width={200}
                    flexGrow={20}
                    flexShrink={1}
                    label="Order Bin"
                    dataKey="orderBin"
                    headerClassName="centered-header"
                    cellRenderer={({ rowData: order }) => (
                      <div className="d-flex align-items-center justify-content-center">
                        {order.picking && order.picking.pickingId ? order.picking.pickingId : '-'}
                      </div>
                    )}
                  />
                )}

                {[1].includes(activeTab) && (
                  <Column
                    width={200}
                    flexGrow={20}
                    flexShrink={1}
                    label="Progress"
                    dataKey="progress"
                    headerClassName="centered-header"
                    cellRenderer={({ rowData: order }) => (
                      <div
                        className="d-flex align-items-center justify-content-center"
                        data-testid={'progress-' + order.sourceOrderId}
                      >
                        {showPickingProgress(order)}
                      </div>
                    )}
                  />
                )}
                <Column
                  width={200}
                  flexGrow={20}
                  flexShrink={1}
                  label={t('common.channel')}
                  dataKey="datePaid"
                  cellRenderer={({ rowData: order }) => t(`common.channels.${order.source}`)}
                />
                <Column
                  width={150}
                  label=""
                  dataKey=""
                  disableSort={true}
                  cellRenderer={({ rowData: order, rowIndex }) => (
                    <div
                      data-testid={'dialog-inspect-row-' + order.sourceOrderId}
                      className="d-flex align-items-center "
                      onClick={() => handleOpenExpandedOrderDialog(order, rowIndex)}
                    >
                      <i className="fa-regular fa-magnifying-glass table-action-icon"></i>
                    </div>
                  )}
                />
                <Column
                  width={300}
                  flexGrow={30}
                  flexShrink={1}
                  label=""
                  dataKey=""
                  disableSort={true}
                  cellRenderer={({ rowData: order, rowIndex }) => (
                    <div className="d-flex align-items-center ">
                      <Box
                        onClick={() => handleExpandRow(order, rowIndex)}
                        style={{ cursor: 'pointer' }}
                        display={'flex'}
                        justifyContent={'between'}
                      >
                        <div className="d-flex align-items-center orders-table-order">
                          <Typography style={{ fontSize: '0.8rem' }}>
                            {order.sourceOrderId}
                          </Typography>
                          <i className="fa-regular fa-chevron-right orders-table-order-icon"></i>
                        </div>
                      </Box>
                    </div>
                  )}
                />
              </Table>
            )}
          </AutoSizer>
        </div>
      )}

      <ReportIssueDialog
        open={openReportIssueDialog}
        onClose={handleCloseReportIssueDialog}
        onConfirm={handleConfirmReportIssueDialog}
        type={selectedArticle ? 'article' : 'order'}
        order={selectedOrder}
        editMode={editIssueMode}
      />
      {selectedOrder && (
        <OrderDetailsDialog
          open={openOrderDetailsDialog}
          onClose={() => setOpenOrderDetailsDialog(false)}
          order={
            filteredOrders.find((o) => o.ptOrderId === selectedOrder.ptOrderId) || selectedOrder
          }
          handleNextOrderDetail={handleNextOrderDetail}
          handlePrevOrderDetail={handlePrevOrderDetail}
          handleConfirmReportIssueDialog={handleConfirmReportIssueDialog}
          handleConfirmSolveIssueDialog={handleConfirmSolveIssueDialog}
          handleChangeOrderState={handleChangeOrderState}
          handleConfirmTrackingNumberDialog={handleConfirmTrackingNumberDialog}
          viewOnly={viewOnly}
        />
      )}

      <SolveIssueDialog
        open={openSolveIssueDialog}
        onClose={handleCloseSolveIssueDialog}
        onConfirm={handleConfirmSolveIssueDialog}
      />

      {selectedOrder && (
        <TrackingNumberDialog
          order={selectedOrder}
          open={openTrackingNumberDialog}
          onClose={handleCloseTrackingNumberDialog}
          onConfirm={handleConfirmTrackingNumberDialog}
        />
      )}

      {selectedOrder && (
        <UnlinkOrderDialog
          open={openUnlinkOrderDialog}
          order={selectedOrder}
          onClose={handleCloseUnlinkOrderDialog}
          onConfirm={handleConfirmUnlinkOrderDialog}
        />
      )}

      {openMessagesPopup ? (
        <NewWindow
          url={(function () {
            const url =
              `https://www.cardmarket.com/en/${
                selectedOrder ? GAME_NAMES[selectedOrder?.articles![0].card?.idGame!] : ''
              }/Account/Messages/${selectedOrder?.buyer?.username}` || ''
            return url
          })()}
        />
      ) : null}
    </>
  )
}
